import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Avatar from "../../../shared/Avatar";
import Card from "../../../shared/Card";
import Select from "../../../shared/Select";
import { EnvelopeIcon, PhoneIcon } from "@heroicons/react/20/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { renderAmount } from "../../../utils";
import EditableText from "../../../shared/EditableText";
import Badge from "../../../shared/Badge";
import DriverComplianceBadge from "../DriverComplianceBadge";
import DriverComplianceSection from "../DriverComplianceSection";
import { buildDriverMetaPayload, formatEndorsements } from "../../../utils/driver-profile.utils";
import {
  fetchOrganizations,
  selectOrganizationsOptions,
} from "../../../redux/reducers/organizations/organizationsSlice";
import { useAuth } from "../../../hooks/useAuth";
import { hydrateDriverFormFromRecord } from "../../../utils/driver-profile.utils";

/**
 * Compact driver profile summary for the Info tab sidebar.
 */
export const DriverProfileSummary = ({ info, handleUpdate }) => {
  async function onSave(e, type) {
    let data = { ...e.data };
    if (type === "meta") {
      data = { meta: e.data };
    }
    handleUpdate(data);
  }

  const shortOrg = info.organization;
  const complianceIssues = info.driverCompliance?.issues || [];

  return (
    <Card className="p-6">
      <div className="flex flex-col items-center text-center">
        <Avatar src={info.avatar} />
        <div className="mt-4 w-full space-y-2">
          <DriverInfoEditable
            id="email"
            icon={EnvelopeIcon}
            value={info.email}
            save={(e) => onSave(e)}
          />
          <DriverInfoEditable
            id="phone"
            icon={PhoneIcon}
            value={info.phone}
            save={(e) => onSave(e)}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        <DriverComplianceBadge driver={info} />
        {info.driverProfile?.employmentStatus && (
          <Badge
            label={info.driverProfile.employmentStatus.replace("_", " ")}
            color="gray"
            size="sm"
          />
        )}
      </div>

      {complianceIssues.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">Compliance alerts</p>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {complianceIssues.slice(0, 4).map((issue) => (
              <li key={issue.code}>{issue.message}</li>
            ))}
          </ul>
        </div>
      )}

      <hr className="my-4" />

      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            CDL
          </h3>
          <p className="mt-1 text-sm text-gray-900">
            {info.driverProfile?.licenseNumber || info.meta?.license || "—"}
          </p>
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Endorsements
          </h3>
          <p className="mt-1 text-sm text-gray-900">
            {formatEndorsements(
              info.driverProfile?.endorsements || info.meta?.endorsements
            )}
          </p>
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Mileage Rate
          </h3>
          <DriverInfoEditable
            displayText={(e) => renderAmount(e)}
            id="mileageRate"
            value={info.meta?.mileageRate}
            save={(e) => onSave(e, "meta")}
            meta={true}
            type="number"
          />
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Hourly Rate
          </h3>
          <DriverInfoEditable
            displayText={(e) => renderAmount(e)}
            id="hourlyRate"
            value={info.meta?.hourlyRate}
            save={(e) => onSave(e, "meta")}
            meta={true}
            type="number"
          />
        </div>
        <div>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Empty Rate
          </h3>
          <DriverInfoEditable
            displayText={(e) => renderAmount(e)}
            id="emptyRate"
            value={info.meta?.emptyRate}
            save={(e) => onSave(e, "meta")}
            meta={true}
            type="number"
          />
        </div>
        {!info.org && (
          <div className="col-span-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Organization
            </h3>
            <div className="mt-1">
              <OrganizationEditable
                organization={shortOrg}
                onSave={(organization) =>
                  handleUpdate({ organization: organization?._id })
                }
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Full-width driver qualification file editor.
 */
export const DriverQualificationPanel = ({ info, handleUpdate }) => {
  const [complianceForm, setComplianceForm] = useState({});

  useEffect(() => {
    if (info?._id) {
      setComplianceForm(hydrateDriverFormFromRecord(info));
    }
  }, [info]);

  function updateComplianceForm({ id, value }) {
    setComplianceForm((prev) => ({ ...prev, [id]: value }));
  }

  function saveComplianceProfile() {
    handleUpdate({ meta: buildDriverMetaPayload(complianceForm) });
  }

  return (
    <Card className="p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
            Driver Qualification File
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            CDL, medical certificate, endorsements, and compliance testing records.
          </p>
        </div>
        <button
          type="button"
          onClick={saveComplianceProfile}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
        >
          Save Credentials
        </button>
      </div>
      <DriverComplianceSection
        form={complianceForm}
        updateForm={updateComplianceForm}
      />
    </Card>
  );
};

/** @deprecated Use DriverProfileSummary + DriverQualificationPanel in page layout. */
const DriverCard = ({ info, handleUpdate }) => (
  <div className="space-y-6">
    <DriverProfileSummary info={info} handleUpdate={handleUpdate} />
    <DriverQualificationPanel info={info} handleUpdate={handleUpdate} />
  </div>
);

const DriverInfoEditable = ({ id, value, save, icon, type, displayText }) => {
  const Icon = icon;
  return (
    <div className="flex items-center justify-center">
      {icon && <Icon className="mr-1 h-4 w-4 shrink-0" />}
      <EditableText
        displayText={(e) => (
          <span className="text-sm">{displayText ? displayText(e) : e}</span>
        )}
        id={id}
        iconClasses="h-3 w-3"
        value={value}
        onSave={(e) => save(e)}
        type={type}
      />
    </div>
  );
};

const OrganizationEditable = ({ organization, onSave }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(organization);

  const organizationsStatus = useSelector(
    (state) => state.organizations.status
  );
  const organizationOptions = useSelector(selectOrganizationsOptions);

  useEffect(() => {
    if (organizationsStatus === "idle") {
      dispatch(fetchOrganizations({ parent: user.parentCompany }));
    }
  }, [organizationsStatus, dispatch, user.parentCompany]);

  useEffect(() => {
    setSelectedOrg(organization);
  }, [organization]);

  const handleSave = () => {
    onSave(selectedOrg);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedOrg(organization);
    setIsEditing(false);
  };

  const getSelectDisplayValue = () => {
    if (!selectedOrg) return "";
    return selectedOrg.name || selectedOrg.label || "";
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <Select
          value={getSelectDisplayValue()}
          placeholder="Select an organization"
          options={organizationOptions}
          onChange={({ value }) => setSelectedOrg(value)}
          className="text-sm"
        />
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
          >
            Save
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {organization ? (
        <Badge
          label={organization.name}
          link={`/organizations/${organization._id}`}
        />
      ) : (
        <span className="text-sm italic text-gray-400">No organization</span>
      )}
      <button
        type="button"
        onClick={() => setIsEditing(true)}
        className="text-gray-400 hover:text-primary-600"
        title="Edit organization"
      >
        <PencilIcon className="h-3 w-3" />
      </button>
    </div>
  );
};

export default DriverCard;
